class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    // Il combobox non esponeva stato né navigazione: aria-expanded era fisso a false nel
    // markup e non esisteva alcun listener da tastiera sulle opzioni (NC-15).
    this.activeOptionIndex = -1;
    this.input.addEventListener('keydown', this.onKeyDown.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    // Nella pagina dei risultati (context "page") la tendina predittiva non deve
    // aprirsi: l'input serve solo a lanciare la ricerca con Invio, mostrando i
    // risultati direttamente nel listing sottostante.
    this.predictiveDisabled = this.dataset.context === 'page';

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  get statusRegion() {
    return this.querySelector('[data-predictive-search-status]');
  }

  get options() {
    return Array.from(this.querySelectorAll('[role="option"]'));
  }

  setExpanded(isExpanded) {
    this.input.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    if (!isExpanded) this.clearActiveOption();
  }

  clearActiveOption() {
    this.activeOptionIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.options.forEach((option) => option.setAttribute('aria-selected', 'false'));
  }

  setActiveOption(index) {
    const options = this.options;
    if (!options.length) return;

    // Le frecce girano in tondo: da fondo lista si torna alla prima opzione.
    if (index < 0) index = options.length - 1;
    if (index >= options.length) index = 0;

    options.forEach((option, i) => option.setAttribute('aria-selected', i === index ? 'true' : 'false'));

    const active = options[index];
    this.input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
    this.activeOptionIndex = index;
  }

  onKeyDown(event) {
    switch (event.key) {
      case 'ArrowDown':
        if (!this.options.length) return;
        event.preventDefault();
        this.setActiveOption(this.activeOptionIndex + 1);
        break;
      case 'ArrowUp':
        if (!this.options.length) return;
        event.preventDefault();
        this.setActiveOption(this.activeOptionIndex - 1);
        break;
      case 'Enter': {
        // Senza opzione attiva l'Invio deve inviare il form come sempre.
        const active = this.options[this.activeOptionIndex];
        const link = active?.querySelector('a[href]');
        if (link) {
          event.preventDefault();
          link.click();
        }
        break;
      }
      case 'Escape':
        this.toggleSearchState(false);
        break;
    }
  }

  announceResults() {
    const region = this.statusRegion;
    if (!region) return;
    const count = this.querySelector('[data-predictive-search-count]');
    region.textContent = count ? count.textContent.trim() : '';
  }

  onFocus(event) {
    // Pagina risultati: nessuna tendina, si cerca con Invio.
    if (this.predictiveDisabled) return;
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    if (this.statusRegion) this.statusRegion.textContent = '';
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    if (this.predictiveDisabled) return;
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    // Pagina risultati: nessuna tendina, si cerca con Invio.
    if (this.predictiveDisabled) return;
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        // Il contenuto della listbox è appena stato sostituito: le vecchie opzioni non
        // esistono più, quindi lo stato attivo va azzerato prima di riesporre l'apertura.
        this.clearActiveOption();
        this.setExpanded(true);
        this.announceResults();

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    this.setExpanded(isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
